/**
 * StructuredTab — the Structured tab of the extract viewer (spec §4, §17.4):
 * fetches the row's structured_json, normalizes it, builds the view, and
 * renders the loading / error / empty states around <StructuredView/>.
 *
 * Swapped in for the old FieldsPane by `USE_STRUCTURED_VIEW` in
 * ExtractDocumentViewer.jsx — flip that flag to go back.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import colors, { status } from "../../../../../../theme/colors";
import { losExtractsApi } from "../../../../../../api/losExtractsApi";
import { normalizeExtract, rowFacts } from "./normalizeExtract";
import { buildView } from "./buildView";
import StructuredView from "./components/StructuredView";

const Skeleton = () => (
  <Box
    aria-busy="true"
    sx={{
      p: 2,
      "@keyframes svPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
      "& .sk": { backgroundColor: colors.slate100, borderRadius: "6px", animation: "svPulse 1.4s ease-in-out infinite" },
      "@media (prefers-reduced-motion: reduce)": { "& .sk": { animation: "none" } },
    }}
  >
    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
      {[64, 80, 72, 90].map((w) => (
        <Box key={w} className="sk" sx={{ width: w, height: 24, borderRadius: "999px !important" }} />
      ))}
    </Box>
    {[0, 1, 2].map((s) => (
      <Box key={s} sx={{ mb: 3 }}>
        <Box className="sk" sx={{ width: 120, height: 14, mb: 1.5 }} />
        {[0, 1, 2, 3].map((r) => (
          <Box key={r} sx={{ display: "grid", gridTemplateColumns: "34% 1fr", gap: 1.5, mb: 1.25 }}>
            <Box className="sk" sx={{ height: 12 }} />
            <Box className="sk" sx={{ height: 12, width: `${60 + ((r * 13) % 35)}%` }} />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

const Message = ({ title, children, tone }) => (
  <Box sx={{ p: 3 }}>
    <Box
      sx={{
        p: 2,
        borderRadius: "10px",
        border: `1px solid ${tone === "crit" ? status.error.bg : colors.slate200}`,
        backgroundColor: tone === "crit" ? status.error.bg : colors.slate50,
      }}
    >
      <Box sx={{ fontSize: 13.5, fontWeight: 600, color: tone === "crit" ? status.error.fg : colors.slate900 }}>{title}</Box>
      {children ? <Box sx={{ fontSize: 12.5, color: colors.slate600, mt: 0.5, lineHeight: 1.5 }}>{children}</Box> : null}
    </Box>
  </Box>
);

const RejectionMessage = ({ rejection, hasMarkdown }) => (
  <Message title="Extraction was rejected" tone="crit">
    {rejection.reason ? <div>{rejection.reason}</div> : null}
    {rejection.documentType ? <div>The extractor thinks this is a {rejection.documentType}.</div> : null}
    {hasMarkdown ? <div>Open the Text tab to see what was read.</div> : null}
  </Message>
);

export default function StructuredTab({ dealId, row, focusField, onShowInText, onRevealSensitive }) {
  const facts = rowFacts(row);
  const blobName = facts.json?.blob_name;
  const [state, setState] = useState({ loading: !!blobName, error: null, text: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!dealId || !blobName) {
      setState({ loading: false, error: null, text: null });
      return undefined;
    }
    let live = true;
    setState({ loading: true, error: null, text: null });
    losExtractsApi
      .getText(dealId, blobName)
      .then((t) => live && setState({ loading: false, error: null, text: t }))
      .catch((e) => live && setState({ loading: false, error: e?.message || "Request failed.", text: null }));
    return () => {
      live = false;
    };
  }, [dealId, blobName, nonce]);

  const norm = useMemo(
    () => (state.text === null ? null : normalizeExtract(state.text, row)),
    // row identity changes on index reload; the text is what matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.text, blobName],
  );
  const view = useMemo(() => (norm && !norm.error ? buildView(norm, { now: new Date() }) : null), [norm]);
  const retry = useCallback(() => setNonce((n) => n + 1), []);

  if (!blobName) {
    if (facts.rejection) return <RejectionMessage rejection={facts.rejection} hasMarkdown={facts.hasMarkdown} />;
    return (
      <Message title="No structured data for this document.">
        {facts.hasMarkdown ? "This document type isn't extracted into fields. The Text tab has the full text." : null}
      </Message>
    );
  }
  if (state.loading) return <Skeleton />;
  if (state.error) {
    return (
      <Message title="Couldn't load the extracted data." tone="crit">
        <div>{state.error}</div>
        <Button size="small" variant="outlined" onClick={retry} sx={{ mt: 1, textTransform: "none" }}>
          Try again
        </Button>
      </Message>
    );
  }
  if (norm?.error) {
    return (
      <Message title="This extract isn't valid JSON, so it can't be shown here.">
        Open the JSON tab to see or fix it.
      </Message>
    );
  }
  if (!view) return <Skeleton />;
  if (view.isEmpty && !view.checks.length) {
    return <Message title="Nothing was extracted from this document." />;
  }
  return (
    <StructuredView
      key={blobName}
      view={view}
      focusField={focusField}
      onShowInText={facts.hasMarkdown ? onShowInText : undefined}
      onRevealSensitive={onRevealSensitive}
    />
  );
}
